import React, { useRef, useState } from 'react';
import { IFabricationProject } from '../../../../../../../store/ducks/fabrication-projects/types';
import RemoveProject from './../RemoveProject';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../../../../../../store';
import EditProject from './../EditProject';
import { Button, Icon } from '@gerdau/hefesto';
import { InformationPanel } from '@gerdau/hefesto-mais-web/InformationPanel';
import { Variants } from '@gerdau/hefesto/_shared/types';
import { FabricationProjectsService } from '../../../../../../../services/FabricationProjects';
import { RcFile } from 'antd/lib/upload';
import { addToast } from '../../../../../../../store/ducks/toast/actions';
import { EToastTypes } from '../../../../../../../store/ducks/toast/types';
import RevisionOfProject from '../../../UploadProjects/RevisionOfProjectModal';
import { createDownloadFile } from '../../../../../../../utils/createDownloadFile';
import { getTaggingInformationScreen } from '../../../../../../../utils/Tagging';
import { useHistory, useLocation } from 'react-router-dom';
import { sendTagging } from '../../../../../../../store/ducks/tagging/actions';
import { ERoutes } from '../../../../../../../routes';
import { EProjectsMockupsRoutes } from '../../../../../projectsMockup.routes';
import {
  RcaKind,
  sendRcaLogs
} from '../../../../../../../store/ducks/observability/actions';

interface IViewOptionsButtonProps {
  contractNumber: string;
  project: IFabricationProject;
  updateProjectsTable: () => void;
}

interface IProjectDownloaded {
  fileBase64?: string;
  loading: boolean;
  error: boolean;
}

interface IOptions {
  text: string;
  handleOnClick: () => void;
  icon?: JSX.Element;
  variant?: Extract<Variants, 'danger' | 'primary'>;
  disabled?: boolean;
}

const ViewOptionsButton: React.FC<IViewOptionsButtonProps> = ({
  contractNumber,
  project,
  updateProjectsTable
}) => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const history = useHistory();

  const { screenName, itemDescription } = getTaggingInformationScreen(
    pathname,
    true
  );

  const projectState = useSelector(
    (state: ApplicationState) => state.fabricationProjects
  );

  const [projectDownloaded, setProjectDownloaded] =
    useState<IProjectDownloaded>({ loading: false, error: false });

  const [uploadedFile, setUploadedFile] = useState<RcFile>();

  const [canShowEditModal, setCanShowEditModal] = useState(false);
  const [canShowRemoveProject, setCanShowRemoveProject] = useState(false);

  const downloadProject = () => {
    FabricationProjectsService.downloadProject(contractNumber, project.id)
      .then((fileBase64: string) => {
        dispatch(sendRcaLogs(RcaKind.DownloadItem, 'PROJ', project.id));
        if (fileBase64) {
          setProjectDownloaded({
            loading: false,
            error: false,
            fileBase64: fileBase64
          });
          createDownloadFile(fileBase64, project.fileName);
          return;
        }
        setProjectDownloaded({ loading: false, error: true });
      })
      .catch((err) => {
        console.error(err);
        setProjectDownloaded({ loading: false, error: true });
      });
  };

  const inputRef = useRef<HTMLInputElement>(null);

  const handleClickUpdateRevision = () => {
    inputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileObj = event.target.files && (event.target.files[0] as RcFile);
    if (!fileObj) {
      return;
    }
    setUploadedFile(fileObj);
  };

  const onCloseRevisionOfProjectModal = () => {
    const message = `O arquivo: ${uploadedFile?.name} foi enviado com sucesso.`;

    dispatch(
      addToast({
        type: EToastTypes.SUCCESS,
        message,
        durationInMS: 5000
      })
    );
    setUploadedFile(undefined);
    updateProjectsTable();
  };

  const options: IOptions[] = [
    {
      text: 'Baixar projeto',
      handleOnClick: () => {
        dispatch(
          sendTagging({
            screenName,
            itemDescription: `${itemDescription}_projetos_menuOpcoes_baixarProjeto`,
            itemName: 'button:baixar_projeto',
            event: 'click'
          })
        );
        downloadProject();
      }
    },
    {
      text: 'Atualizar revisão',
      handleOnClick: () => {
        dispatch(
          sendTagging({
            screenName,
            itemDescription: `${itemDescription}_projetos_menuOpcoes_atualizarRevisao`,
            itemName: 'button:atualizar_revisao',
            event: 'click'
          })
        );
        handleClickUpdateRevision();
      }
    },
    {
      text: 'Ver histórico',
      handleOnClick: () => {
        dispatch(
          sendTagging({
            screenName,
            itemDescription: `${itemDescription}_projetos_menuOpcoes_verHistorico`,
            itemName: 'button:ver_historico',
            event: 'click'
          })
        );
        history.push(
          `${ERoutes.PROJECT_MOCKUP}${EProjectsMockupsRoutes.PROJECT_HISTORY}`,
          { project }
        );
      }
    },
    {
      text: 'Editar',
      handleOnClick: () => {
        dispatch(
          sendTagging({
            screenName,
            itemDescription: `${itemDescription}_projetos_menuOpcoes_editar`,
            itemName: 'button:editar',
            event: 'click'
          })
        );
        setCanShowEditModal(true);
      }
    },
    {
      text: 'Apagar',
      handleOnClick: () => {
        dispatch(
          sendTagging({
            screenName,
            itemDescription: `${itemDescription}_projetos_menuOpcoes_apagar`,
            itemName: 'button:apagar',
            event: 'click'
          })
        );
        setCanShowRemoveProject(true);
      },
      disabled: project.hasOrders || !!project.nextRevisionId
    }
  ];

  return (
    <>
      <InformationPanel
        title="Opções"
        position={'bottom right'}
        panelOptions={options}
        trigger={
          <div data-testid="projects-view-options-button">
            <Button
              name="options"
              variant="neutral"
              icon={<Icon option="MoreVertical" />}
            />
          </div>
        }
      ></InformationPanel>
      <input
        style={{ display: 'none' }}
        data-testid={'uploadFile'}
        ref={inputRef}
        type="file"
        onChange={handleFileChange}
        accept=".dxf,.dwg,.dwf,.pdf"
      />
      {!!uploadedFile && (
        <RevisionOfProject
          uploadedFile={uploadedFile}
          projectState={projectState}
          closeModal={onCloseRevisionOfProjectModal}
          revisionOfProjectId={project.id}
          onCancel={() => setUploadedFile(undefined)}
        />
      )}

      {canShowEditModal && (
        <EditProject
          project={project}
          contractNumber={contractNumber}
          onCancel={() => setCanShowEditModal(false)}
          updateProjectsTable={updateProjectsTable}
        />
      )}

      {canShowRemoveProject && (
        <RemoveProject
          projectId={project.id}
          projectName={project.fileName}
          contractNumber={contractNumber}
          onCancel={() => setCanShowRemoveProject(false)}
          updateProjectsTable={updateProjectsTable}
        />
      )}
    </>
  );
};

export default ViewOptionsButton;
